import {Box, Button, IconButton, Image, Modal, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text} from "@chakra-ui/react";
import * as React from "react";
import {FC, useRef, useState} from "react";
import QRCode from "react-qr-code";
import {BiDownload, BiPrinter, BiQr} from "react-icons/bi";

type Props = {
    value: string,
    legacyValue?: string,
    label?: string
}

const MARK_SRC = "/branding/soundspots-mark.svg";

const QrPanel: FC<{title: string; subtitle: string; value: string; onClick?: () => void}> = ({title, subtitle, value, onClick}) => (
    <Stack
        align={"center"}
        spacing={2}
        flex={1}
        minW={"180px"}
        cursor={onClick ? "pointer" : undefined}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        _hover={onClick ? {opacity: 0.85} : undefined}
    >
        <Text fontSize={"sm"} fontWeight={"semibold"}>{title}</Text>
        <Text fontSize={"xs"} color={"gray.600"}>{subtitle}</Text>
        <Box bg={"white"} p={3} borderRadius={"md"} borderWidth={"1px"} borderColor={"gray.200"}>
            <QRCode value={value} level={"L"} size={170}/>
        </Box>
    </Stack>
);

const PRINT_QR_PX = 480;
const MARK_RATIO = 0.22; // logo width as a fraction of the QR width

const BrandedQr: FC<{value: string; size: number}> = ({value, size}) => {
    const markSize = Math.round(size * MARK_RATIO);
    return (
        <Box position={"relative"} width={`${size}px`} height={`${size}px`}>
            <QRCode value={value} level={"H"} size={size}/>
            <Box
                position={"absolute"}
                top={"50%"}
                left={"50%"}
                transform={"translate(-50%, -50%)"}
                width={`${markSize}px`}
                height={`${markSize}px`}
                bg={"white"}
                borderRadius={"md"}
                p={1}
            >
                <Image src={MARK_SRC} alt={"SoundSpots"} width={"100%"} height={"100%"}/>
            </Box>
        </Box>
    );
};

const printQr = (value: string, label: string, sourceSvg: SVGSVGElement | null) => {
    if (!sourceSvg) return;
    const win = window.open("", "_blank", "width=720,height=820");
    if (!win) {
        return;
    }
    const cloned = sourceSvg.cloneNode(true) as SVGSVGElement;
    cloned.setAttribute("width", "100%");
    cloned.setAttribute("height", "100%");
    cloned.removeAttribute("style");
    const svgMarkup = new XMLSerializer().serializeToString(cloned);
    const safeLabel = label.replace(/[<&>]/g, (c) => ({"<": "&lt;", ">": "&gt;", "&": "&amp;"}[c] || c));
    const markUrl = `${window.location.origin}${MARK_SRC}`;
    win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>${safeLabel} — SoundSpots</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 32px; color: #1E160A; background: #FDFBF7; display: flex; flex-direction: column; align-items: center; }
  h1 { font-size: 22px; margin: 0 0 16px 0; text-align: center; }
  .qr-wrap { position: relative; width: ${PRINT_QR_PX}px; height: ${PRINT_QR_PX}px; background: #fff; }
  .qr-wrap svg { display: block; width: 100%; height: 100%; }
  .mark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: ${Math.round(PRINT_QR_PX * MARK_RATIO)}px; height: ${Math.round(PRINT_QR_PX * MARK_RATIO)}px; background: #fff; border-radius: 8px; padding: 4px; box-sizing: border-box; }
  .mark img { width: 100%; height: 100%; display: block; }
  .credit { font-size: 13px; color: #555; margin-top: 18px; text-align: center; letter-spacing: 0.02em; }
  .credit a { color: inherit; text-decoration: none; }
  @media print { body { background: #fff; } }
</style>
</head>
<body>
  <h1>${safeLabel}</h1>
  <div class="qr-wrap">
    ${svgMarkup}
    <div class="mark"><img id="mark-img" src="${markUrl}" alt="SoundSpots"/></div>
  </div>
  <div class="credit">powered by SoundSpots &middot; <a href="https://soundspots.com">soundspots.com</a></div>
  <script>
    (function() {
      function go() { setTimeout(function() { window.focus(); window.print(); setTimeout(function() { window.close(); }, 300); }, 100); }
      var img = document.getElementById("mark-img");
      if (img && !img.complete) {
        img.addEventListener("load", go);
        img.addEventListener("error", go);
      } else {
        go();
      }
    })();
  <\/script>
</body>
</html>`);
    win.document.close();
};

const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const TEXT_COLOR = "#1E160A";
const CREDIT_COLOR = "#555555";
const PNG_PAD = 32;
const PNG_HEADING_GAP = 18;
const PNG_HEADING_FONT_PX = 22;
const PNG_CREDIT_GAP = 20;
const PNG_CREDIT_FONT_PX = 14;

const downloadPng = async (value: string, label: string, sourceSvg: SVGSVGElement | null) => {
    if (!sourceSvg) return;
    const size = PRINT_QR_PX;
    const markSize = Math.round(size * MARK_RATIO);
    const markPad = 4;

    const cloned = sourceSvg.cloneNode(true) as SVGSVGElement;
    cloned.setAttribute("width", String(size));
    cloned.setAttribute("height", String(size));
    const svgString = new XMLSerializer().serializeToString(cloned);
    const svgBlob = new Blob([svgString], {type: "image/svg+xml;charset=utf-8"});
    const svgUrl = URL.createObjectURL(svgBlob);

    const canvasWidth = size + PNG_PAD * 2;
    const canvasHeight = PNG_PAD + PNG_HEADING_FONT_PX + PNG_HEADING_GAP + size + PNG_CREDIT_GAP + PNG_CREDIT_FONT_PX + PNG_PAD;
    const qrX = PNG_PAD;
    const qrY = PNG_PAD + PNG_HEADING_FONT_PX + PNG_HEADING_GAP;
    const headingY = PNG_PAD + Math.round(PNG_HEADING_FONT_PX * 0.85);
    const creditY = qrY + size + PNG_CREDIT_GAP + Math.round(PNG_CREDIT_FONT_PX * 0.85);

    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        URL.revokeObjectURL(svgUrl);
        return;
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Heading: space name
    ctx.fillStyle = TEXT_COLOR;
    ctx.font = `600 ${PNG_HEADING_FONT_PX}px ${FONT_STACK}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(label, canvasWidth / 2, headingY, canvasWidth - PNG_PAD * 2);

    await new Promise<void>((resolve, reject) => {
        const qrImg = new window.Image();
        qrImg.onload = () => {
            ctx.drawImage(qrImg, qrX, qrY, size, size);
            URL.revokeObjectURL(svgUrl);
            resolve();
        };
        qrImg.onerror = (event) => {
            URL.revokeObjectURL(svgUrl);
            reject(event);
        };
        qrImg.src = svgUrl;
    });

    await new Promise<void>((resolve) => {
        const markImg = new window.Image();
        markImg.crossOrigin = "anonymous";
        markImg.onload = () => {
            const x = qrX + (size - markSize) / 2;
            const y = qrY + (size - markSize) / 2;
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(x, y, markSize, markSize);
            ctx.drawImage(markImg, x + markPad, y + markPad, markSize - markPad * 2, markSize - markPad * 2);
            resolve();
        };
        markImg.onerror = () => {
            // If the logo can't be drawn (CORS or missing), still produce a QR without the centre mark.
            resolve();
        };
        markImg.src = `${window.location.origin}${MARK_SRC}`;
    });

    // Credit line: powered by SoundSpots · soundspots.com
    ctx.fillStyle = CREDIT_COLOR;
    ctx.font = `400 ${PNG_CREDIT_FONT_PX}px ${FONT_STACK}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("powered by SoundSpots \u00B7 soundspots.com", canvasWidth / 2, creditY, canvasWidth - PNG_PAD * 2);

    canvas.toBlob((blob) => {
        if (!blob) return;
        const a = document.createElement("a");
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = `${label.replace(/[^a-z0-9-_]+/gi, "_") || "soundspots"}-qr.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, "image/png");
};

export const QrCodeLink: FC<Props> = ({value, legacyValue, label}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isPrintOpen, setIsPrintOpen] = useState<boolean>(false);
    const printSourceRef = useRef<HTMLDivElement | null>(null);
    const showLegacy = !!legacyValue && legacyValue !== value;
    const safeLabel = label || "SoundSpots space";

    const openPrint = () => setIsPrintOpen(true);
    const handlePrint = () => {
        const svg = printSourceRef.current?.querySelector("svg") as SVGSVGElement | null;
        printQr(value, safeLabel, svg);
    };
    const handleDownload = () => {
        const svg = printSourceRef.current?.querySelector("svg") as SVGSVGElement | null;
        downloadPng(value, safeLabel, svg).catch((error) => console.warn("[qr] download failed", error));
    };

    return <>
        <IconButton aria-label={"Get QR Code"} variant={"ghost"} icon={<BiQr/>} onClick={() => setIsOpen(true)}/>

        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size={showLegacy ? "2xl" : "md"} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{safeLabel}</ModalHeader>
                <ModalCloseButton/>
                <Box px={6} pb={6}>
                    <Stack direction={{base: "column", md: showLegacy ? "row" : "column"}} spacing={6} align={"center"} justify={"center"}>
                        <QrPanel
                            title={"App 5.0 or newer"}
                            subtitle={"Click to print"}
                            value={value}
                            onClick={openPrint}
                        />
                        {showLegacy ? (
                            <QrPanel
                                title={"App 3.x – 4.x"}
                                subtitle={"In-app scanner only"}
                                value={legacyValue!}
                            />
                        ) : null}
                    </Stack>
                    {showLegacy ? (
                        <Text fontSize={"xs"} color={"gray.500"} mt={4} textAlign={"center"}>
                            Print both side by side until v5 has rolled out across your audience.
                        </Text>
                    ) : null}
                </Box>
            </ModalContent>
        </Modal>

        <Modal isOpen={isPrintOpen} onClose={() => setIsPrintOpen(false)} size={"lg"} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{safeLabel} — print</ModalHeader>
                <ModalCloseButton/>
                <Box px={6} pb={6}>
                    <Stack align={"center"} spacing={4}>
                        <Box ref={printSourceRef} data-qr-print-source>
                            <BrandedQr value={value} size={320}/>
                        </Box>
                        <Stack direction={"row"} spacing={3}>
                            <Button leftIcon={<BiPrinter/>} onClick={handlePrint} colorScheme={"blue"}>
                                Print
                            </Button>
                            <Button leftIcon={<BiDownload/>} onClick={handleDownload} variant={"outline"}>
                                Download PNG
                            </Button>
                        </Stack>
                        <Text fontSize={"xs"} color={"gray.500"} textAlign={"center"}>
                            The printed QR carries the SoundSpots mark in the centre. Error correction
                            level H keeps it scannable even with the logo overlay.
                        </Text>
                    </Stack>
                </Box>
            </ModalContent>
        </Modal>
    </>;
};
